/**
 * Open and close the app-level detail sheets that `AppLayout` renders.
 *
 * The sheets are driven by URL params (`?personSheet=`, `?companySheet=`,
 * `?dealSheet=`) rather than local state, so every caller produces a shareable
 * link and the originating page stays mounted underneath.
 *
 * This exists because that `setSearchParams` dance was copied into three
 * places — `AppLayout`, `FavoritesPage` and `AskCoffeePanel` — and the
 * notification feed would have made a fourth. Each copy is a chance to forget
 * `{ replace: true }` (which would push a history entry per sheet open) or to
 * clobber unrelated params by not using the functional updater.
 *
 * No backend surface: this reads and writes URL params only. There is no query
 * to skip in preview and no dummy data to preserve, so it behaves identically
 * in preview and production.
 *
 * Deliberately does NOT return `searchParams`. Callers that read params keep
 * their own `useSearchParams()`; a second call in the same tree is
 * context-backed and free. Keeping reads out means adopting this hook never
 * changes what a component re-renders on.
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/** The detail sheets `AppLayout` renders from URL params. */
export type EntitySheetKey = "personSheet" | "companySheet" | "dealSheet" | "taskSheet";

/**
 * Params that only mean something while their sheet is open, cleared with it. `suggestion`
 * auto-opens the job-change review dialog in the person sheet; leaving it behind would reopen
 * that dialog the next time ANY person sheet opened.
 */
const SHEET_EXTRAS: Partial<Record<EntitySheetKey, readonly string[]>> = {
  personSheet: ["suggestion"],
};

export interface EntitySheetParams {
  /** `extra` sets companion params in the same URL update, e.g. `{ suggestion: "12" }`. */
  openEntitySheet: (key: EntitySheetKey, id: string, extra?: Record<string, string>) => void;
  closeEntitySheet: (key: EntitySheetKey) => void;
}

export function useEntitySheetParams(): EntitySheetParams {
  const [, setSearchParams] = useSearchParams();

  const openEntitySheet = useCallback(
    (key: EntitySheetKey, id: string, extra?: Record<string, string>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(key, id);
          // Opening a sheet without extras must not inherit a previous sheet's.
          for (const name of SHEET_EXTRAS[key] ?? []) next.delete(name);
          for (const [name, value] of Object.entries(extra ?? {})) next.set(name, value);
          return next;
        },
        // replace, not push: opening a sheet should not add a history entry, or
        // Back walks the user through every sheet they glanced at.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeEntitySheet = useCallback(
    (key: EntitySheetKey) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete(key);
          for (const name of SHEET_EXTRAS[key] ?? []) next.delete(name);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { openEntitySheet, closeEntitySheet };
}
